import { PogNonCircular } from "@CommonTypes/merchflow/pog/pog";
import { AgentApplyScope, PogAgentRunResponse } from "./types";

/**
 * What the one button after a turn does. `kind` names the copy; the rest is
 * what the replay sends. A resume carries the offset the failed page started
 * at, so it never re-runs the pages that already wrote.
 */
export type FollowUpKind =
  | "applyPog"
  | "applyPogAsNew"
  | "applyMerchflow"
  | "retryPog"
  | "resumeMerchflow";

export interface FollowUpAction {
  kind: FollowUpKind;
  scope: AgentApplyScope;
  /** PUT with save_as_new: a new store POG version rather than an in-place write. */
  saveAsNew?: boolean;
  /** First page to ask for; only a resume sets it. */
  applyOffset?: number;
  /** Merchflow size for the resume label. */
  total?: number;
}

/**
 * A planHandover can be replayed as a write only when it describes a layout
 * edit. QA plans come back as `qa_code` — read-only Python that answers a
 * question — so they never get an Apply button.
 */
export const canApplyHandover = (response: PogAgentRunResponse): boolean => {
  if (!response.planHandover || response.planHandover.kind === "qa_code") return false;
  if (response.mode === "preview" && response.diff.length === 0) return false;
  return response.intent === "postprocess";
};

// Same target as the agent's debug UI: the merchflow pass writes each store's
// latest POG, whichever version that is.
const APPLY_MERCHFLOW: FollowUpAction = { kind: "applyMerchflow", scope: "merchflow" };

/**
 * The optional way to apply to this store: as a new version. Offered only next
 * to the in-place single-POG apply, the same way the page's Save button offers
 * "Save New Version" — never for the merchflow pass, which would mint a new
 * version in every store at once.
 */
export const APPLY_POG_AS_NEW_VERSION: FollowUpAction = {
  kind: "applyPogAsNew",
  scope: "pog",
  saveAsNew: true,
};

export const alternativeFor = (action: FollowUpAction): FollowUpAction | null =>
  action.scope === "pog" && !action.saveAsNew ? APPLY_POG_AS_NEW_VERSION : null;

/**
 * The one Apply button to offer after a turn, mirroring the agent's debug UI:
 * preview offers the single POG, and a single-POG apply that landed then
 * offers the rest of the merchflow. A single-POG apply that failed offers a
 * retry instead — rolling a plan out to every store when it could not be
 * written to this one would be the wrong next step. A merchflow apply that
 * stopped part-way offers to resume from the first page that did not finish.
 * Anything else offers nothing.
 *
 * `isPlanOnCurrentPog` covers the other way this store can already hold the
 * plan: the merchandiser saved the preview themselves. Applying it again would
 * re-run the plan on a POG that already has it, which is a write for nothing —
 * and for a plan that is not idempotent, such as reversing a shelf, a wrong one.
 */
export const followUpFor = (
  response: PogAgentRunResponse,
  isPlanOnCurrentPog = false,
): FollowUpAction | null => {
  if (!canApplyHandover(response)) return null;

  if (response.mode === "preview") {
    return isPlanOnCurrentPog ? APPLY_MERCHFLOW : { kind: "applyPog", scope: "pog" };
  }

  if (response.mode !== "apply") return null;

  if (response.scope === "pog") {
    // The rollout needs a row that says this POG holds the plan: written, saved
    // as new, or skipped because it already did. A failure or no row at all
    // leaves that unconfirmed, so the only sensible next step is to try here again.
    const counts = countApplyResults(response);
    const confirmed = counts.written + counts.newCreated + counts.skipped > 0;

    return confirmed ? APPLY_MERCHFLOW : { kind: "retryPog", scope: "pog" };
  }

  const batch = response.applyBatch;
  if (response.scope === "merchflow" && batch && batch.nextOffset !== null) {
    return {
      kind: "resumeMerchflow",
      scope: "merchflow",
      applyOffset: batch.nextOffset,
      total: batch.total,
    };
  }

  return null;
};

/**
 * The edited planogram a preview can put on the canvas, or null when there is
 * nothing to show. Apply turns are excluded: their result is already on screen
 * after the refetch, and QA never edits a layout.
 */
export const previewPogFor = (response: PogAgentRunResponse): PogNonCircular | null => {
  if (response.mode !== "preview" || response.intent !== "postprocess") return null;
  if (!response.diff.length) return null;

  return response.updatedPog;
};

export interface ApplyCounts {
  written: number;
  /** Save-as-new writes: a new store POG was created rather than this one overwritten. */
  newCreated: number;
  skipped: number;
  failed: number;
  total: number;
}

export const countApplyResults = (response: PogAgentRunResponse): ApplyCounts => {
  const counts: ApplyCounts = {
    written: 0,
    newCreated: 0,
    skipped: 0,
    failed: 0,
    total: response.results.length,
  };

  for (const result of response.results) {
    if (result.status === "new-created") counts.newCreated += 1;
    else counts[result.status] += 1;
  }

  return counts;
};

/**
 * Whether the planogram on screen is now stale. Any successful write to the POG
 * the user is looking at means the canvas has to be reloaded from OWA — the
 * agent writes through OWA and does not touch the canvas itself.
 */
export const hasWrittenCurrentPog = (
  response: PogAgentRunResponse,
  currentPogId: number,
): boolean => {
  if (response.mode !== "apply") return false;

  return response.results.some(
    (result) => result.status === "written" && result.target.pogId === currentPogId,
  );
};

/**
 * One response for a batched apply. The agent hands back one page of `results`
 * per request; the thread wants a single card, so the pages are merged: every
 * row and every warning, with the message left blank for the card to sum up in
 * the merchandiser's language — the agent's own text is per-page progress
 * ("batch 51~100 of total 120 POGs"), which is status, not a result.
 *
 * `stoppedAt` is the offset of a page that failed after these had written.
 * It survives as `applyBatch.nextOffset`, so the follow-up is a resume from
 * exactly there rather than a replay from the start over stores already done.
 */
export const combineApplyPages = (
  pages: PogAgentRunResponse[],
  stoppedAt: number | null = null,
): PogAgentRunResponse => {
  const last = pages[pages.length - 1];
  const results = pages.flatMap((page) => page.results);
  const batch = pages.find((page) => page.applyBatch)?.applyBatch ?? null;

  return {
    ...last,
    message: "",
    results,
    warnings: pages.flatMap((page) => page.warnings),
    // A merged run is never a single-POG apply, so the top-level diff does not apply.
    diff: [],
    skillTrace: pages.flatMap((page) => page.skillTrace),
    applyBatch: batch
      ? { total: batch.total, offset: 0, count: results.length, nextOffset: stoppedAt }
      : null,
  };
};

/**
 * The store POG a single-POG apply created when it ran as save-as-new, or null.
 * The agent reports the new id in `target.pogId` of that one result; the POG on
 * screen was left untouched, so the page moves to the new version instead of
 * refetching. Only scope `pog` can be trusted here — a merchflow pass rewrites
 * every target's id, so nothing ties one of them back to the screen.
 */
export const createdPogIdFor = (response: PogAgentRunResponse): number | null => {
  if (response.mode !== "apply" || response.scope !== "pog") return null;

  const [only] = response.results;
  if (!only || response.results.length !== 1 || only.status !== "new-created") return null;

  return only.target.pogId;
};
